import React from "react";
import { INotificationKeysValues } from "../../../models/Interface/NotificationManagement.interface";
import { MonoText, PreviewImage } from "./NotificationCells";

const MissingValue = ({ label }: { label: string }) => (
  <span className="text-sm font-semibold" style={{ color: "#B42318" }}>
    — missing {label} —
  </span>
);

const PreviewMetaRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-baseline gap-10 min-w-0">
    <span
      className="text-xs font-semibold flex-shrink-0"
      style={{ color: "#9CA3AF", width: 68 }}
    >
      {label}
    </span>
    <span className="min-w-0" style={{ wordBreak: "break-word" }}>
      {children}
    </span>
  </div>
);

/**
 * Lock-screen style mock of a key set — the shape a recipient actually sees.
 * Shared by the send form and the localized preview so both QA the same render.
 */
const PushPreview = ({
  entry,
  showMeta = true,
  emptyHint,
}: {
  entry?: INotificationKeysValues;
  showMeta?: boolean;
  emptyHint?: string;
}) => {
  if (!entry) {
    return (
      <div
        className="flex items-center justify-center rounded-xl text-sm text-center px-20"
        style={{
          border: "1px dashed #D5D5D5",
          background: "#FCFCFE",
          color: "#9CA3AF",
          minHeight: 220,
        }}
      >
        {emptyHint ??
          "Pick a notification type to preview the push exactly as it will be delivered."}
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-20 flex flex-col gap-20"
      style={{ background: "#F5F6FA", border: "1px solid #EEF0F4" }}
    >
      <div
        className="rounded-xl p-10 flex gap-10 items-start"
        style={{
          background: "#fff",
          boxShadow: "0 6px 18px rgba(32, 34, 36, 0.08)",
        }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0 font-bold"
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            background: "#2d3ac7",
            color: "#fff",
            fontSize: 16,
          }}
        >
          T
        </div>

        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <div className="flex items-center justify-between gap-10">
            <span
              className="text-xs font-semibold"
              style={{ color: "#6b7280", letterSpacing: "0.04em" }}
            >
              TALKPAL
            </span>
            <span className="text-xs" style={{ color: "#9CA3AF" }}>
              now
            </span>
          </div>
          {entry.title ? (
            <span
              className="text-sm font-semibold text-dark-grey"
              style={{ lineHeight: 1.35, wordBreak: "break-word" }}
            >
              {entry.title}
            </span>
          ) : (
            <MissingValue label="title" />
          )}
          {entry.description ? (
            <span
              className="text-sm"
              style={
                {
                  color: "#3F4654",
                  lineHeight: 1.4,
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                } as React.CSSProperties
              }
            >
              {entry.description}
            </span>
          ) : (
            <MissingValue label="description" />
          )}
        </div>

        <PreviewImage src={entry.url} size={44} />
      </div>

      {showMeta && (
        <div className="flex flex-col gap-10 min-w-0">
          <PreviewMetaRow label="Title key">
            <MonoText value={entry.titleKey} />
          </PreviewMetaRow>
          <PreviewMetaRow label="Fallback">
            <span className="text-sm text-dark-grey" style={{ opacity: 0.75 }}>
              {entry.fallback || "—"}
            </span>
          </PreviewMetaRow>
          <PreviewMetaRow label="Deeplink">
            {entry.deeplink ? (
              <MonoText value={entry.deeplink} />
            ) : (
              <span className="text-sm" style={{ color: "#9CA3AF" }}>
                none on this type
              </span>
            )}
          </PreviewMetaRow>
        </div>
      )}
    </div>
  );
};

export default PushPreview;
